import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  appStoreAppURL,
  appStoreDeveloperURL,
  createPublisherSyncJob,
  getPublisher,
  getPublisherEventsForPublisher,
  listPublisherApps,
  triggerJob,
  type Publisher,
  type PublisherApp,
  type PublisherEvent,
} from '../api/client';
import { AppError } from '../api/request';
import { panelPath } from '../config/paths';
import PageBackButton from './PageBackButton';
import { PublisherEventListItem } from './publisherEventUI';
import { SkeletonDetail } from './Skeleton';

const PAGE_SIZE = 20;

function isPresentDate(iso?: string): boolean {
  if (!iso) return false;
  return !iso.startsWith('0001-01-01');
}

function sortApps(apps: PublisherApp[]): PublisherApp[] {
  return [...apps].sort((a, b) => {
    if (a.isRemoved !== b.isRemoved) return a.isRemoved ? 1 : -1;
    return (a.trackName || '').localeCompare(b.trackName || '', undefined, { sensitivity: 'base' });
  });
}

const PublisherDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [publisher, setPublisher] = useState<Publisher | null>(null);
  const [apps, setApps] = useState<PublisherApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [events, setEvents] = useState<PublisherEvent[]>([]);
  const [cursorBefore, setCursorBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    if (!id) return;
    setLoadingEvents(true);
    setEventsError(null);
    try {
      const res = await getPublisherEventsForPublisher(id, { limit: PAGE_SIZE });
      const items = res.items ?? [];
      setEvents(items);
      setCursorBefore(items.length > 0 ? items[items.length - 1]!.createdAt : null);
      setHasMore(items.length === PAGE_SIZE);
    } catch (e) {
      setEventsError(e instanceof Error ? e.message : t('publishers.loadError'));
    } finally {
      setLoadingEvents(false);
    }
  }, [id, t]);

  const load = useCallback(async () => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const [p, appsRes] = await Promise.all([getPublisher(id), listPublisherApps(id)]);
      setPublisher(p);
      setApps(appsRes.items ?? []);
    } catch (e) {
      if (e instanceof AppError && e.code === 'ERR_NOT_FOUND') {
        setNotFound(true);
        setPublisher(null);
      } else {
        setError(e instanceof Error ? e.message : t('publishers.loadError'));
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
    void loadEvents();
  }, [load, loadEvents]);

  const loadMoreEvents = useCallback(async () => {
    if (!id || loadingMore || loadingEvents || !hasMore) return;
    setLoadingMore(true);
    setEventsError(null);
    try {
      const res = await getPublisherEventsForPublisher(id, {
        before: cursorBefore ?? undefined,
        limit: PAGE_SIZE,
      });
      const newItems = res.items ?? [];
      if (newItems.length === 0) {
        setHasMore(false);
        return;
      }
      setEvents((prev) => [...prev, ...newItems]);
      setCursorBefore(newItems[newItems.length - 1]!.createdAt);
      setHasMore(newItems.length === PAGE_SIZE);
    } catch (e) {
      setEventsError(e instanceof Error ? e.message : t('publishers.loadError'));
    } finally {
      setLoadingMore(false);
    }
  }, [id, cursorBefore, hasMore, loadingEvents, loadingMore, t]);

  const sortedApps = useMemo(() => sortApps(apps), [apps]);

  const handleSyncNow = useCallback(async () => {
    if (!id) return;
    setSyncing(true);
    setError(null);
    try {
      const { jobId } = await createPublisherSyncJob(id);
      triggerJob(jobId).catch((er) => setError(er instanceof Error ? er.message : t('publishers.syncError')));
      setTimeout(() => {
        void load();
        void loadEvents();
      }, 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('publishers.syncError'));
    } finally {
      setSyncing(false);
    }
  }, [id, load, loadEvents, t]);

  if (!id || notFound) {
    return (
      <div className="p-4 max-w-5xl mx-auto space-y-4">
        <PageBackButton to={panelPath('/publishers')} />
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('publishers.detailNotFound')}</p>
      </div>
    );
  }

  if (loading && !publisher) {
    return (
      <div className="p-4 max-w-5xl mx-auto space-y-4">
        <PageBackButton to={panelPath('/publishers')} />
        <SkeletonDetail />
      </div>
    );
  }

  if (!publisher) {
    return (
      <div className="p-4 max-w-5xl mx-auto space-y-4">
        <PageBackButton to={panelPath('/publishers')} />
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
      </div>
    );
  }

  const developerURL = appStoreDeveloperURL(publisher.storefront, publisher.artistId);

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <PageBackButton to={panelPath('/publishers')} />
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {publisher.displayName || publisher.artistId}
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('publishers.detailSubtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={developerURL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t('publishers.openInStore')}
          </a>
          <button
            type="button"
            className="btn btn-secondary px-3 py-1.5 text-xs disabled:opacity-50"
            disabled={syncing}
            onClick={() => void handleSyncNow()}
          >
            {syncing ? t('publishers.syncing') : t('publishers.syncNow')}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          {t('publishers.detailInfoTitle')}
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.colName')}</dt>
            <dd className="mt-0.5 text-gray-900 dark:text-white">{publisher.displayName || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.artistId')}</dt>
            <dd className="mt-0.5 text-gray-900 dark:text-white font-mono text-xs">{publisher.artistId}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.colStorefront')}</dt>
            <dd className="mt-0.5 text-gray-900 dark:text-white uppercase">{publisher.storefront}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.colTotalApps')}</dt>
            <dd className="mt-0.5 text-gray-900 dark:text-white tabular-nums">
              {(publisher.totalApps ?? 0).toLocaleString()}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.colLastApp')}</dt>
            <dd className="mt-0.5 text-gray-900 dark:text-white">
              {publisher.lastAppName ? (
                <>
                  {publisher.lastAppTrackId ? (
                    <a
                      href={appStoreAppURL(publisher.storefront, publisher.lastAppTrackId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {publisher.lastAppName}
                    </a>
                  ) : (
                    publisher.lastAppName
                  )}
                  {isPresentDate(publisher.lastAppReleaseDate) && (
                    <span className="block text-xs text-gray-400 dark:text-gray-500">
                      {new Date(publisher.lastAppReleaseDate!).toLocaleDateString()}
                    </span>
                  )}
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 dark:text-gray-400">{t('publishers.colLastSync')}</dt>
            <dd className="mt-0.5 text-gray-900 dark:text-white">
              {isPresentDate(publisher.lastSyncAt)
                ? new Date(publisher.lastSyncAt!).toLocaleString()
                : t('publishers.neverSynced')}
              {publisher.lastSyncStatus === 'errored' && (
                <span className="ml-2 text-red-500">({t('publishers.lastSyncErrored')})</span>
              )}
            </dd>
          </div>
        </dl>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('publishers.appsTitle')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('publishers.appsHint')}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{t('publishers.appColName')}</th>
                <th className="px-4 py-3">{t('publishers.appColVersion')}</th>
                <th className="px-4 py-3">{t('publishers.appColGenre')}</th>
                <th className="px-4 py-3">{t('publishers.appColRating')}</th>
                <th className="px-4 py-3">{t('publishers.appColPrice')}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && sortedApps.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                    …
                  </td>
                </tr>
              )}
              {!loading && sortedApps.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                    {t('publishers.appsEmpty')}
                  </td>
                </tr>
              )}
              {sortedApps.map((app) => {
                const storeURL = appStoreAppURL(publisher.storefront, app.trackId);
                return (
                  <tr
                    key={app.trackId}
                    className={`text-gray-700 dark:text-gray-300 ${app.isRemoved ? 'opacity-50' : ''}`}
                  >
                    <td className="px-4 py-2">
                      <a
                        href={storeURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {app.trackName || app.trackId}
                      </a>
                      {app.isRemoved && (
                        <span className="ml-2 text-xs text-gray-400">({t('publishers.appRemoved')})</span>
                      )}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-xs">{app.version || '—'}</td>
                    <td className="px-4 py-2 text-xs">{app.primaryGenreName || '—'}</td>
                    <td className="px-4 py-2 text-xs whitespace-nowrap">
                      {app.userRatingCount > 0
                        ? `${app.averageUserRating.toFixed(1)} (${app.userRatingCount.toLocaleString()})`
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs whitespace-nowrap">
                      {app.formattedPrice || (app.price === 0 ? t('publishers.free') : String(app.price))}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <a
                        href={storeURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 dark:hover:text-primary-400"
                        aria-label={t('publishers.openInStore')}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('publishers.detailTimelineTitle')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {t('publishers.detailTimelineHint')}
          </p>
        </div>
        {eventsError && (
          <div className="px-4 py-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
            {eventsError}
          </div>
        )}
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {loadingEvents && events.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-500 text-sm">…</li>
          )}
          {!loadingEvents && events.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-500 text-sm">
              {t('publishers.detailTimelineEmpty')}
            </li>
          )}
          {events.map((ev) => (
            <PublisherEventListItem key={ev.id} event={ev} storefront={publisher.storefront} />
          ))}
        </ul>
        {hasMore && events.length > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center">
            <button
              type="button"
              className="btn btn-secondary px-4 py-2 text-sm disabled:opacity-50"
              disabled={loadingMore}
              onClick={() => void loadMoreEvents()}
            >
              {loadingMore ? t('publishers.loadingMore') : t('publishers.loadMore')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublisherDetailPage;
